import axios from "axios"
import { useStoreActions, useStoreState } from "easy-peasy"
import { useCallback, useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import { beep } from "./beep.js"
import ErrorPopup from "./ErrorPopup.js"
import StatusPopup from "./StatusPopup.js"
import {
	CLIENT_USERS_URL,
	DOG_AGENT_ADD_URL,
	DOG_AGENT_DEL_URL,
	DOG_AGENT_LIST_URL,
} from "./Constants.js"

const DogAssignStaff = () => {
	const navigate = useNavigate()

	// easy-peasy
	const Token = useStoreState((state) => state.Token)
	const Theme = useStoreState((state) => state.Theme)
	const textColor = Theme === 4 ? "#333333" : "white"
	const setLoggedIn = useStoreActions((actions) => actions.setLoggedIn)
	const setHeaderCity = useStoreActions((actions) => actions.setHeaderCity)
	const setHeaderUser = useStoreActions((actions) => actions.setHeaderUser)

	const [clientUsers, setClientUsers] = useState([])
	const [agentList, setAgentList] = useState([])
	const [error, setError] = useState("")

	const refreshData = useCallback(async () => {
		const request = {
			login_id: Token.login_id,
			client_id: Token.client_id,
		}
		try {
			const usersRes = await axios.post(CLIENT_USERS_URL, request)
			setClientUsers(usersRes.data)
		} catch (err) {
			setError(err.message)
		}
		try {
			const agentsRes = await axios.post(DOG_AGENT_LIST_URL, request)
			setAgentList(agentsRes.data)
		} catch (err) {
			setError(err.message)
		}
	}, [Token.login_id, Token.client_id])

	useEffect(() => {
		refreshData()
	}, [refreshData])

	// Error timeout / session redirect
	useEffect(() => {
		if (!error) return
		beep()
		const timer = setTimeout(() => {
			if (error.includes("401")) {
				setHeaderCity("CR Cloud Technology")
				setHeaderUser("None")
				setLoggedIn(false)
				navigate("/login")
			}
		}, 2000)
		return () => clearTimeout(timer)
	}, [error, navigate, setHeaderCity, setHeaderUser, setLoggedIn])

	async function handleAgentAdd(userId) {
		if (!userId) return
		try {
			const user = clientUsers.find(
				(u) => String(u.id ?? u[0]) === String(userId),
			)
			const lastname = user.lastname ?? user[2]
			const firstname = user.firstname ?? user[3]
			const duplicate = agentList.some(
				(a) =>
					(a.lastname ?? a[2]).toLowerCase() === lastname.toLowerCase() &&
					(a.firstname ?? a[3]).toLowerCase() === firstname.toLowerCase(),
			)
			if (duplicate) return
			const request = {
				login_id: Token.login_id,
				client_id: Token.client_id,
				client_user_id: Number(userId),
				lastname,
				firstname,
			}
			await axios.post(DOG_AGENT_ADD_URL, request)
			await refreshData()
		} catch (err) {
			setError(err.message)
		}
	}

	async function handleAgentDelete(agentId) {
		try {
			const request = {
				login_id: Token.login_id,
				client_id: Token.client_id,
				agent_id: agentId,
			}
			await axios.post(DOG_AGENT_DEL_URL, request)
			await refreshData()
		} catch (err) {
			if (err.message === "Request failed with status code 500") {
				setError("Request failed because packets have been assigned.")
			} else {
				setError(err.message)
			}
		}
	}


	return (
		<main className="Main">
			<fieldset className="tag_assignstaff">
				<legend>Assign Staff as Deputies to Sell Tags</legend>
				<div style={{ display: "flex", gap: "2rem", alignItems: "flex-start" }}>
					<div>
						<label
							style={{
								display: "block",
								marginBottom: "0.5rem",
								textAlign: "center",
							}}
						>
							<span style={{ color: textColor }}>Available Staff</span>
							<br />
							<span style={{ fontSize: "0.8rem" }}>Click row to add</span>
						</label>
						<table>
							<thead>
								<tr>
									<th style={{ backgroundColor: "var(--table-header-grey)" }}>
										Last Name
									</th>
									<th style={{ backgroundColor: "var(--table-header-grey)" }}>
										First Name
									</th>
								</tr>
							</thead>
							<tbody>
								{clientUsers.map((u) => (
									<tr
										key={u[0]}
										onClick={() => {
											setError("")
											handleAgentAdd(u[0])
										}}
										style={{ cursor: "pointer" }}
									>
										<td style={{ height: "28px" }}>{u[2]}</td>
										<td style={{ height: "28px" }}>{u[3]}</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
					<div>
						<label
							style={{
								display: "block",
								marginBottom: "0.5rem",
								textAlign: "center",
							}}
						>
							<span style={{ color: textColor }}>Assigned Deputies</span>
							<br />
							<span style={{ fontSize: "0.8rem" }}>Click row to remove</span>
						</label>
						{agentList.length > 0 ? (
							<table>
								<thead>
									<tr>
										<th style={{ backgroundColor: "var(--table-header-grey)" }}>
											Last Name
										</th>
										<th style={{ backgroundColor: "var(--table-header-grey)" }}>
											First Name
										</th>
									</tr>
								</thead>
								<tbody>
									{agentList.map((a) => (
										<tr
											key={a.id ?? a[0]}
											onClick={() => handleAgentDelete(a.id ?? a[0])}
										>
											<td style={{ height: "28px" }}>{a.lastname ?? a[2]}</td>
											<td style={{ height: "28px" }}>{a.firstname ?? a[3]}</td>
										</tr>
									))}
								</tbody>
							</table>
						) : (
							<StatusPopup message="No agents assigned." />
						)}
					</div>
				</div>
				<ErrorPopup
				message={
					error === "Timeout" ? "Your session has timed out. Please login again." :
					error === "422" ? "Unable to use browser refresh button." :
					error === "500" ? "Unknown error — notify CR Cloud Technology." :
					error
				}
				onClose={() => setError("")}
			/>
			</fieldset>
		</main>
	)
}

export default DogAssignStaff
